import { useEffect, useRef, useState } from 'react';
import { geoContains, geoDistance, geoGraticule10, geoOrthographic, geoPath } from 'd3-geo';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
import { feature } from 'topojson-client';
import type { Topology } from 'topojson-specification';
import type { LonLat } from '../lib/introTimeline.ts';

/**
 * Orthographic globe on a 2D canvas (d3-geo): no WebGL requirement, no external tile servers, no keys.
 * Country shapes: Natural Earth via `world-atlas` (public domain data), 1:110m first and 1:50m once the
 * view zooms in (loaded lazily). The camera's country is highlighted; the marker sits on the real
 * coordinates and is hidden when on the far side.
 */
type Countries = FeatureCollection<Geometry, { name?: string }>;
let coarse: Promise<Countries> | null = null;
let fine: Promise<Countries> | null = null;
const load = (which: '110m' | '50m') =>
  (which === '110m' ? import('world-atlas/countries-110m.json') : import('world-atlas/countries-50m.json')).then((m) => {
    const topo = (m as { default: unknown }).default as Topology;
    return feature(topo, topo.objects.countries!) as unknown as Countries;
  });

export interface GlobeProps {
  center: LonLat;
  zoom: number;
  target: LonLat | null;
  markerOpacity: number;
  /** Camera-country highlight strength (0..1). */
  countryOpacity?: number;
  /** Pulse phase for the marker ring (0..1), driven by the caller's clock. */
  pulse: number;
  label?: string;
}

export function Globe({ center, zoom, target, markerOpacity, countryOpacity = 1, pulse, label }: GlobeProps) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [lo, setLo] = useState<Countries | null>(null);
  const [hi, setHi] = useState<Countries | null>(null);
  const [size, setSize] = useState({ w: 640, h: 360 });

  useEffect(() => {
    let live = true;
    coarse ??= load('110m');
    void coarse.then((c) => live && setLo(c));
    return () => {
      live = false;
    };
  }, []);
  useEffect(() => {
    if (zoom < 3 || hi) return;
    let live = true;
    fine ??= load('50m');
    void fine.then((c) => live && setHi(c));
    return () => {
      live = false;
    };
  }, [zoom, hi]);
  useEffect(() => {
    const el = canvas.current?.parentElement;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.clientWidth, h: el.clientHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const cv = canvas.current;
    const ctx = cv?.getContext('2d');
    if (!cv || !ctx) return;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    cv.width = Math.round(size.w * dpr);
    cv.height = Math.round(size.h * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const base = Math.min(size.w, size.h) * 0.42;
    const proj = geoOrthographic()
      .rotate([-center.lon, -center.lat])
      .scale(base * zoom)
      .translate([size.w / 2, size.h / 2])
      .clipAngle(90)
      .precision(0.3);
    const path = geoPath(proj, ctx);
    const css = getComputedStyle(cv);
    const col = (v: string, d: string) => css.getPropertyValue(v).trim() || d;
    ctx.clearRect(0, 0, size.w, size.h);
    // Atmosphere and ocean.
    const r = base * zoom;
    const g = ctx.createRadialGradient(size.w / 2, size.h / 2, r * 0.9, size.w / 2, size.h / 2, r * 1.12);
    g.addColorStop(0, 'rgba(66,217,232,0.25)');
    g.addColorStop(1, 'rgba(66,217,232,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.arc(size.w / 2, size.h / 2, r * 1.12, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    path({ type: 'Sphere' });
    ctx.fillStyle = col('--globe-ocean', '#0c1a26');
    ctx.fill();
    ctx.beginPath();
    path(geoGraticule10());
    ctx.strokeStyle = 'rgba(154,167,182,0.12)';
    ctx.lineWidth = 0.6;
    ctx.stroke();
    const countries = zoom >= 3 && hi ? hi : lo;
    if (countries) {
      const home = target ? countries.features.find((f: Feature<Geometry, { name?: string }>) => geoContains(f, [target.lon, target.lat])) : undefined;
      ctx.beginPath();
      for (const f of countries.features) if (f !== home || countryOpacity <= 0) path(f);
      ctx.fillStyle = col('--globe-land', '#1f2b38');
      ctx.fill();
      ctx.strokeStyle = 'rgba(237,242,247,0.18)';
      ctx.lineWidth = 0.6;
      ctx.stroke();
      if (home && countryOpacity > 0) {
        ctx.beginPath();
        path(home);
        ctx.fillStyle = col('--globe-land', '#1f2b38');
        ctx.fill();
        ctx.fillStyle = `rgba(215,255,63,${0.22 * countryOpacity})`;
        ctx.fill();
        ctx.strokeStyle = `rgba(215,255,63,${0.8 * countryOpacity})`;
        ctx.lineWidth = 1.2;
        ctx.stroke();
      }
    }
    // Marker at the real coordinates (not drawn when on the far side of the globe).
    if (target && markerOpacity > 0 && geoDistance([center.lon, center.lat], [target.lon, target.lat]) < Math.PI / 2) {
      const p = proj([target.lon, target.lat]);
      if (p) {
        ctx.globalAlpha = markerOpacity;
        ctx.beginPath();
        ctx.arc(p[0], p[1], 6 + 18 * pulse, 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(215,255,63,${0.8 * (1 - pulse)})`;
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(p[0], p[1], 5, 0, Math.PI * 2);
        ctx.fillStyle = '#d7ff3f';
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }
  }, [center.lon, center.lat, zoom, target, markerOpacity, countryOpacity, pulse, lo, hi, size]);

  return <canvas ref={canvas} className="globe-canvas" role="img" aria-label={label ?? 'Globe'} data-testid="globe" data-countries={lo ? (zoom >= 3 && hi ? '50m' : '110m') : 'loading'} />;
}
